import { ClientEvents } from 'common/events/ClientEvents';
import { Slugs } from 'common/urls/Slugs';
import { isEmpty } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { Maybe } from 'src/gqlReactTypings.generated.d';
import { useHideNavbar } from 'src/pages/consumer/components/DishFirstHeader/store';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useIsOnSearchPage } from 'src/shared/utils/SearchUtils';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { useSearchInputActions } from '../../../store/hooks/useSearchInput';
import { useSearchQueryState } from '../../../store/hooks/useSearchQuery';
import { SearchDataContext } from '../../../store/SearchDataContext';
import { SearchInput } from '../SearchInput';
import { BackButton, BackButtonIcon, Container, InputContainer, SearchIcon, SearchInputWrapper } from './styles';

interface IProps {
  hideSearch: () => void;
  isSearchShowing: boolean;
  timeZone: Maybe<string>;
  showSearch: () => void;
}

export const SearchBarContents: React.FC<IProps> = React.memo((props) => {
  const { hideSearch, isSearchShowing, showSearch } = props;
  const { hideAll } = useHideNavbar();
  const history = useHistory();
  const tracker = useTracker();
  const { handleQueryChange, handleClearSearch, isSearchHidden, activeZipCode } = useContext(SearchDataContext);
  const { blurSearchInput, focusSearchInput } = useSearchInputActions();
  const searchQuery = useSearchQueryState();

  const { isOnSearchPage, isOnSearchPageMobile } = useIsOnSearchPage();
  const { isSmallScreen } = useScreenContext();
  const scrolltopTimeout = useRef(0);

  const homePath = useMemo(() => {
    if (!isEmpty(activeZipCode)) {
      return getSluggedPath(Routes.CONSUMER_EXPLORE, [{ slug: Slugs.ZIP_CODE, value: activeZipCode ?? '' }]);
    }
    return Routes.CONSUMER_LANDING;
  }, [activeZipCode]);

  const handleSearchFocus = useCallback(() => {
    hideAll();
    showSearch();
    tracker.track(ClientEvents.NAV_CLICK, { elementName: 'search-bar' });
  }, [hideAll, showSearch, tracker]);

  const handleBackButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      hideSearch();

      if (isOnSearchPageMobile) {
        history.push(homePath);
      }
    },
    [history, homePath, isOnSearchPageMobile, hideSearch]
  );

  const onSubmit = useCallback(() => {
    if (isOnSearchPage) {
      handleQueryChange(searchQuery, false);
    } else {
      const path = getSluggedPath(Routes.CONSUMER_ORDER_SEARCH, [{ slug: Slugs.ZIP_CODE, value: activeZipCode ?? '' }]);
      history.push(path + history.location.search);
      handleQueryChange(searchQuery, false);
    }
    hideSearch();
  }, [activeZipCode, handleQueryChange, history, isOnSearchPage, searchQuery, hideSearch]);

  useEffect(() => () => {
    clearTimeout(scrolltopTimeout.current);
  });

  return isSearchHidden ? null : (
    <>
      <Container isOpen={isSearchShowing || isOnSearchPageMobile}>
        <InputContainer onClick={handleSearchFocus}>
          <BackButton onClick={handleBackButtonClick}>
            <BackButtonIcon />
          </BackButton>
          <SearchInputWrapper>
            <SearchInput
              searchQuery={searchQuery}
              onBlur={blurSearchInput}
              onFocus={() => {
                focusSearchInput();
                if (!isSearchShowing) {
                  handleSearchFocus();
                }
                if (isSmallScreen) {
                  // eslint-disable-next-line functional/immutable-data -- Prevent input centering on screen.
                  scrolltopTimeout.current = window.setTimeout(() => {
                    window.scroll(0, 1);
                  }, 100);
                }
              }}
              onQueryChange={handleQueryChange}
              onClearSearch={handleClearSearch}
              placeholder='Search dishes, shefs, cuisines'
              icon={<SearchIcon />}
              onSubmit={onSubmit}
            />
          </SearchInputWrapper>
        </InputContainer>
      </Container>
    </>
  );
});
